import { RefObject } from "react";
import { ArtworkCard } from "@/components/ArtworkCard";
import { ProductGridSkeleton } from "@/components/ProductGridSkeleton";
import { LoadingSpinner } from "@/components/LoadingSpinner";

function getPrimaryImage(artwork: any): string {
  const images = artwork.artwork_images || [];
  if (images.length === 0) return "/placeholder.svg";
  
  // Sort: mockups first, then display_order
  const sorted = [...images].sort((a: any, b: any) => {
    if (a.is_room_mockup && !b.is_room_mockup) return -1;
    if (!a.is_room_mockup && b.is_room_mockup) return 1;
    return (a.display_order || 0) - (b.display_order || 0);
  });
  
  return sorted[0].image_url || "/placeholder.svg";
}

interface ExploreResultsProps {
  isLoading: boolean;
  artworks: any[];
  totalCount: number;
  isFetchingNextPage: boolean;
  observerTarget: RefObject<HTMLDivElement>;
}

export function ExploreResults({
  isLoading,
  artworks,
  totalCount,
  isFetchingNextPage,
  observerTarget,
}: ExploreResultsProps) {
  if (isLoading) {
    return <ProductGridSkeleton count={12} />;
  }

  if (!artworks || artworks.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-16 sm:py-20 text-center px-4">
        <p className="mb-2 text-lg sm:text-xl font-semibold">Inga konstverk hittades</p>
        <p className="text-base text-muted-foreground">
          Prova att ändra dina sökfilter eller kom tillbaka senare
        </p>
      </div>
    );
  }

  return (
    <>
      <p className="mb-3 sm:mb-4 md:mb-6 text-xs sm:text-sm text-muted-foreground">
        Visar {artworks.length} av {totalCount} konstverk
      </p>
      <div className="grid gap-3 sm:gap-4 md:gap-6 grid-cols-2 lg:grid-cols-4">
        {artworks.map((artwork) => (
          <ArtworkCard
            key={artwork.id}
            id={artwork.id}
            slug={artwork.slug}
            title={artwork.title}
            artistName={(artwork.public_profiles as any)?.username || artwork.public_profiles?.full_name || "Okänd konstnär"}
            price={Number(artwork.price)}
            imageUrl={getPrimaryImage(artwork)}
            category={artwork.category}
            artworkType={artwork.artwork_type}
            stockQuantity={artwork.stock_quantity}
            minVariantPrice={artwork.min_variant_price}
          />
        ))}
      </div>

      <div ref={observerTarget} className="h-20 flex items-center justify-center mt-8">
        {isFetchingNextPage && (
          <LoadingSpinner text="Laddar fler konstverk..." />
        )}
      </div>
    </>
  );
}
